function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe",
    ),
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true,
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this)),
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this)),
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)',
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (
        this.mainDetailsToggle &&
        this.mainDetailsToggle.hasAttribute('open') &&
        !this.mainDetailsToggle.contains(document.activeElement)
      ) {
        this.closeMenuDrawer();
      }
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    if (detailsElement) {
      this.closeSubmenu(detailsElement);
    }
  }

  closeSubmenu(detailsElement) {
    if (!detailsElement) return;

    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();

    this.closeDrawerBtn = this.querySelector('.menu-drawer__close');
    this.summaryEl = this.querySelector('header-drawer .header__icon--menu');

    if (this.closeDrawerBtn && this.summaryEl) {
      this.closeDrawerBtn.addEventListener('click', (event) => {
        this.closeMenuDrawer(event, this.summaryEl);
      });
    }
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`,
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`,
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved || this.classList.contains('global-popup-modal')) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`),
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.componentId = this.id;
    this.paginationIcon = this.querySelector('.swiper-pagination-icon .icon');
    this.init();
    window.addEventListener('resize', debounce(this.init.bind(this), 500));
    document.addEventListener('shopify:section:load', this.init.bind(this));
  }

  init() {
    let enableSlider;
    this.paginationType = this.dataset.paginationType;
    if (this.dataset.slider) {
      enableSlider = JSON.parse(this.dataset.slider);
    }
    if (enableSlider) {
      this.initSlider();
    }
  }

  initSlider() {
    this.slider = new Swiper(`.swiper-${this.componentId}`, {
      slidesPerView: this.dataset.slidesPerViewMobile || 1,
      direction: this.dataset.direction,
      spaceBetween: this.dataset.spaceBetweenMobile || 0,
      loop: this.dataset.loop == 'true' ? true : false,
      centeredSlides: this.dataset.centeredSlides == 'true' ? true : false,
      initialSlide: this.dataset.centeredSlides == 'true' ? 1 : 0,
      speed: 500,
      updateOnWindowResize: true,
      observer: true,
      observeParents: true,
      watchSlidesProgress: true,
      normalizeSlideIndex: true,
      preventInteractionOnTransition: true,
      pagination: {
        el: `.swiper-pagination-${this.componentId}`,
        clickable: true,
        type: this.paginationType || 'bullets',
        renderBullet: function (index, className) {
          return `
            <span class="${className}"> 
              <svg
                class="icon icon-pagination"
                width="19"
                height="19"
                viewBox="0 0 19 19"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="9.5" cy="9.5" r="9" fill="white" stroke="#121212"/>
              </svg>
            </span>
          `;
        },
        renderProgressbar: function (progressbarFillClass) {
          return `<span class="${progressbarFillClass}"></span>`;
        },
        renderFraction: function (currentClass, totalClass) {
          return `<span class="${currentClass}"></span> / <span class="${totalClass}"></span>`;
        },
      },
      navigation: {
        nextEl: `.swiper-next-button-${this.componentId}`,
        prevEl: `.swiper-prev-button-${this.componentId}`,
        disabledClass: 'swiper-button-disabled',
      },
      scrollbar: {
        el: `.swiper-scrollbar-${this.componentId}`,
        draggable: true,
      },
      thumbs: {
        swiper:
          this.nextElementSibling != null && this.nextElementSibling.classList.contains('thumbnails')
            ? this.nextElementSibling
            : '',
      },
      mousewheel: this.classList.contains('thumbnails') ? true : false,
      breakpoints: {
        // only for announcement bar
        550: {
          slidesPerView: this.dataset.slidesPerViewMobile550
            ? this.dataset.centeredSlides == 'true'
              ? Math.floor(parseInt(this.dataset.slidesPerViewMobile550))
              : this.dataset.slidesPerViewMobile550
            : this.dataset.slidesPerViewDesktop,
          spaceBetween: this.dataset.spaceBetweenMobile || 0,
        },
        750: {
          slidesPerView: this.dataset.slidesPerViewMobile750
            ? this.dataset.centeredSlides == 'true'
              ? Math.floor(parseInt(this.dataset.slidesPerViewMobile750))
              : this.dataset.slidesPerViewMobile750
            : this.dataset.slidesPerViewDesktop,
          spaceBetween: this.dataset.spaceBetweenMobile || 0,
        },
        1100: {
          slidesPerView: this.dataset.slidesPerViewMobile1100
            ? this.dataset.centeredSlides == 'true'
              ? Math.floor(parseInt(this.dataset.slidesPerViewMobile1100))
              : this.dataset.slidesPerViewMobile1100
            : this.dataset.slidesPerViewDesktop,
          spaceBetween: this.dataset.spaceBetweenMobile || 0,
        },
        1280: {
          autoplay: this.dataset.autoplayMobileOnly == 'true' ? this.dataset.autoplayMobileOnly : false,
          slidesPerView:
            this.dataset.centeredSlides == 'true'
              ? Math.floor(parseInt(this.dataset.slidesPerViewDesktop))
              : this.dataset.slidesPerViewDesktop,
          spaceBetween: this.dataset.spaceBetweenDesktop || 0,
          enabled: this.dataset.disabled == 'true' ? false : true,
        },
      },
      autoplay:
        this.dataset.autoplay == 'true'
          ? {
              delay: parseInt(this.dataset.autoplaySpeed),
              disableOnInteraction: true,
              pauseOnMouseEnter: true,
            }
          : false,
    });
    let swiper = document.querySelector(`.swiper-${this.componentId}`)?.swiper;

    if (swiper) {
      // Fix navigation state management for all browsers (Chrome, Safari, etc.)
      swiper.on('slideChange', () => {
        if (swiper.navigation) {
          swiper.navigation.update();
        }
      });

      swiper.on('transitionEnd', () => {
        if (swiper.navigation) {
          swiper.navigation.update();
        }
      });

      swiper.on('slideChangeTransitionStart', () => {
        // remove color of ::selection
        // bug of swiper that randomly trigger ::selection color of an element, not fixed yet, this is a workaround
        document.body.classList.add('selection--transparent');
      });

      swiper.on('slideChangeTransitionEnd', () => {
        setTimeout(() => {
          document.body.classList.remove('selection--transparent');
        }, 2000);
      });
    }
  }
}
customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true },
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class CardColorSwatches extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange);
    this.cardLink = this.closest('.card__content').querySelector('.card__heading a');
    this.cardImage = this.closest('.card-wrapper ').querySelector('.card__media img');
    this.formId = this.closest('.card__content').querySelector('.product-variant-id');
    this.label = this.closest('.card__content').querySelector('.form__label span');
    this.variantData = JSON.parse(this.querySelector('[type="application/json"]').textContent);
    this.swatch = this.querySelector('.swatch-input__label');
    this.inputs = this.querySelectorAll('.swatch-input__input');
  }

  onVariantChange(event) {
    this.inputs.forEach((input) => {
      input.classList.remove('active');
    });
    this.updateProductCard(event);
  }

  updateProductCard({ target }) {
    const { value } = target;
    target.classList.add('active');
    let defaultLink = this.cardLink.dataset.link;
    let currentVariant = this.variantData[target.dataset.index];
    let currentVariantImage = target.dataset.variantImage;
    if (currentVariantImage) {
      this.cardImage.src = currentVariantImage;
      this.cardImage.srcset = currentVariantImage;
    }
    this.cardLink.href = defaultLink + '?variant=' + currentVariant.id;
    this.formId.value = currentVariant.id;
    if (this.label) {
      this.label.textContent = value;
    }
  }
}

customElements.define('card-color-swatches', CardColorSwatches);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);
      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;
    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset',
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId,
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
    this.showHideRelevantSection();
  }

  showHideRelevantSection() {
    if (document.querySelector('.product--unavailable')) {
      if (this.classList.contains('unavailable-product')) {
        this.classList.remove('hidden');
      } else {
        this.classList.add('hidden');
      }
    } else if (document.querySelector('.product--available')) {
      if (this.classList.contains('unavailable-product')) {
        this.classList.add('hidden');
      } else {
        this.classList.remove('hidden');
      }
    }
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' },
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class HotSpots extends HTMLElement {
  constructor() {
    super();
    this.el = this;
    this.buttons = this.querySelectorAll('[data-button]');
    this.hotspotBlocks = this.querySelectorAll('[data-hotspot-block]');

    if (this.buttons.length > 0 && this.hotspotBlocks.length > 0) {
      this.bindEvents();
    }
  }

  bindEvents() {
    this.buttons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        const id = button.dataset.button;
        this.showContent(id);
      });
    });
  }

  showContent(id) {
    this.hotspotBlocks.forEach((block) => {
      if (block.dataset.hotspotBlock === id) {
        block.classList.add('is-active');
      } else {
        block.classList.remove('is-active');
      }
    });
  }
}

customElements.define('hot-spots', HotSpots);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class ShippingBar extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.thresholdAmount = parseFloat(this.getAttribute('threshold-amount'));
    this.progressBarBg = this.getAttribute('progress-bar-bg');
    this.progressBarColor = this.getAttribute('progress-bar-color');
    this.shippingBarText = this.getAttribute('shipping-bar-text');
    this.shippingBarSuccess = this.getAttribute('shipping-bar-success');

    this.initStyles();
    this.updateShippingBar();
  }

  initStyles() {
    const progressBar = this.querySelector('.shipping-bar__bar');
    const progressBarProgress = this.querySelector('.shipping-bar__progress');
    if (progressBar) {
      progressBar.style.backgroundColor = this.progressBarBg;
    }
    if (progressBarProgress) {
      progressBarProgress.style.backgroundColor = this.progressBarColor;
    }
  }

  updateShippingBar() {
    fetch('/cart.js')
      .then((response) => response.json())
      .then((data) => {
        this.calculateProgress(data.total_price);
      })
      .catch((error) => console.error('Error updating shipping bar:', error));
  }

  calculateProgress(currentVal) {
    const progressBar = this.querySelector('.shipping-bar__progress');
    const progressOuter = this.querySelector('.shipping-bar__remain');
    const successMsg = this.querySelector('.shipping-bar__success');
    const result = Math.round((100 * currentVal) / this.thresholdAmount);

    const shippingBarText = this.getAttribute('shipping-bar-text');
    const shippingBarSuccess = this.getAttribute('shipping-bar-success');
    const shippingBarCurrency = this.getAttribute('shipping-bar-currency');

    if (progressBar) {
      progressBar.style.width = `${result}%`;
    }

    let remainingAmount = this.thresholdAmount - currentVal;
    if (remainingAmount <= 0) {
      progressOuter.style.display = 'none';
      successMsg.style.display = 'block';
      successMsg.textContent = shippingBarSuccess;
    } else {
      let formattedAmount = new Intl.NumberFormat('fr-FR', {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(remainingAmount / 100);
      progressOuter.style.display = 'block';
      successMsg.style.display = 'none';
      progressOuter.innerHTML = shippingBarText.replace('$', formattedAmount + ' ' + shippingBarCurrency);
    }
  }
}

customElements.define('shipping-bar', ShippingBar);

class WishlistHandler extends HTMLElement {
  constructor() {
    super();
    this.addButton = this.querySelector('[data-add-to-wishlist]');
    this.removeButton = this.querySelector('[data-remove-from-wishlist]');
    this.shopUrl = window.Shopify.shop;
    this.customerId = this.dataset.customerId;
    this.productReference = this.dataset.productReference;
    this.productsCount = document.getElementById('wishlist-products-count');
    this.countBubble = document.querySelector('.wishlist-count-bubble');
    this.bindEvents();
  }

  bindEvents() {
    this.addButton.addEventListener('click', (event) => {
      event.preventDefault();
      if (!this.customerId) {
        window.location.href = '/account/login';
        return;
      }
      this.updateWishlist('add');
    });

    this.removeButton.addEventListener('click', (event) => {
      event.preventDefault();
      this.updateWishlist('remove');
    });
  }

  updateWishlist(action) {
    if (this.operationInProgress) {
      return;
    }

    this.operationInProgress = true;

    const url = window.digismoothieWishlistApp.appUrl;
    const data = {
      shop_url: this.shopUrl,
      customer_id: this.customerId,
      product_reference: this.productReference,
      action: action,
    };

    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })
      .then((response) => response.json())
      .then((data) => {
        if (action === 'add') {
          this.addButton.classList.add('hidden');
          this.removeButton.classList.remove('hidden');
          this.removeButton.classList.add('in-wishlist');
        } else {
          this.removeButton.classList.add('hidden');
          this.addButton.classList.remove('hidden');
        }

        if (window.location.pathname === '/pages/wishlist' && this.closest('.wishlist-item')) {
          this.closest('.wishlist-item').remove();
        }

        this.productsCount.innerText = data.products_count;
        this.countBubble.classList.toggle('hidden', data.products_count === 0);

        this.removeButton.addEventListener('animationend', () => {
          this.removeButton.classList.remove('in-wishlist');
        });

        this.operationInProgress = false;
      })
      .catch((error) => {
        console.error('Error:', error);

        this.operationInProgress = false;
      });
  }
}

customElements.define('wishlist-handler', WishlistHandler);

class HeaderCustom extends HTMLElement {
  constructor() {
    super();
    this.el = this;
    this.openDropdown = this.openDropdown.bind(this);
    this.closeDropdown = this.closeDropdown.bind(this);

    if (window.matchMedia('(min-width: 990px)').matches) {
      this.enableDropdownHover();
    }
  }

  enableDropdownHover() {
    this.querySelectorAll('header-custom details').forEach((details) => {
      details.addEventListener('mouseenter', this.openDropdown);
      details.addEventListener('mouseleave', this.closeDropdown);
    });
  }

  openDropdown(event) {
    const details = event.currentTarget;
    details.setAttribute('open', true);
    details.querySelector('summary').setAttribute('aria-expanded', 'true');
  }

  closeDropdown(event) {
    const details = event.currentTarget;
    details.removeAttribute('open');
    details.querySelector('summary').setAttribute('aria-expanded', 'false');
  }
}

customElements.define('header-custom', HeaderCustom);

class BrandsList extends HTMLElement {
  constructor() {
    super();
    this.el = this;
    this.loadMoreButton = this.querySelector('.js-load-more-button');
    this.allBrands = this.querySelectorAll('.brands-list__list-item.hidden');
    this.brandsPerClick = 6;
    this.currentIndex = 0;

    this.loadMoreButtonEvents();
  }

  loadMoreButtonEvents() {
    if (!this.loadMoreButton) return;
    this.loadMoreButton.addEventListener('click', () => {
      const nextBrands = Array.from(this.allBrands).slice(this.currentIndex, this.currentIndex + this.brandsPerClick);

      nextBrands.forEach((brand) => {
        brand.classList.remove('hidden');
        brand.classList.add('visible');
      });

      this.currentIndex += this.brandsPerClick;

      if (this.currentIndex >= this.allBrands.length) {
        this.loadMoreButton.style.display = 'none';
      }
    });
  }
}

customElements.define('brands-list', BrandsList);

function setCookie(name, value, days) {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value}; expires=${expires.toUTCString()}; path=/`;
}

function getCookie(name) {
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i].trim();
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
}

function deleteCookie(name) {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
}

class NewsletterPopup extends HTMLElement {
  constructor() {
    super();
    this.popupAlreadyTriggered = false;

    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });

    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }

    this.updateProductPageVisit();
    this.showPopupAfterProductVisit();
    this.setupBlogSectionObserver();
  }

  connectedCallback() {
    if (this.moved || this.classList.contains('global-popup-modal')) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show() {
    if (this.popupAlreadyTriggered) return;

    this.popupAlreadyTriggered = true;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');

    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
    setCookie('newsletterPopupClosed', 'true', 7);
  }

  updateProductPageVisit() {
    const isProductPage = document.querySelector('.product');

    if (isProductPage) {
      let visits = parseInt(getCookie('productPageVisits') || '0', 10);
      visits++;
      setCookie('productPageVisits', visits, 7);
    }
  }

  showPopupAfterProductVisit() {
    const hasVisitedProductPage = parseInt(getCookie('productPageVisits') || '0', 10) > 0;
    const hasClosedPopup = getCookie('newsletterPopupClosed') === 'true';
    const currentURL = window.location.href;

    const isCartPage = currentURL.includes('/cart');
    const isFormPage = currentURL.includes('/form');
    const isProductPage = document.querySelector('.product');
    if (
      hasVisitedProductPage &&
      !hasClosedPopup &&
      !this.popupAlreadyTriggered &&
      !isCartPage &&
      !isFormPage &&
      !isProductPage
    ) {
      setTimeout(() => {
        this.show();
      }, 5000);
    }
  }

  setupBlogSectionObserver() {
    const blogSection = document.querySelector('.homepage .blog');
    if (!blogSection) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.popupAlreadyTriggered && getCookie('newsletterPopupClosed') !== 'true') {
            this.show();
            observer.disconnect();
          }
        });
      },
      { threshold: 0.5 },
    );

    observer.observe(blogSection);
  }
}

customElements.define('newsletter-popup', NewsletterPopup);

class SeoBrandText extends HTMLElement {
  constructor() {
    super();
    this.el = this;
    this.carMakeFilters = null;
    this.activeFilters = document.querySelector('.active-facets');
    // this.collectionTitles = document.querySelectorAll('h1');
  }

  connectedCallback() {
    if (this.el.classList.contains('seo-brand-text--collection')) {
      this.observeFilterChanges();
      this.updateFilters();

      this.addEventListener('click', (event) => {
        if (event.target.closest('facet-remove')) {
          this.updateFilters();
        }
      });
    } else {
      const text = this.el.querySelector('.seo-brand-text__text');
      if (text.classList.contains('text-empty')) {
        this.el.classList.add('hidden');
      } else {
        this.el.classList.remove('hidden');
      }
    }
  }

  updateCollectionTitle(id) {
    const pathname = window.location.pathname;
    const carMakes = window.carMakes || [];

    const isExactMakePage = carMakes.some((make) => {
      return pathname === `/collections/${make.handle}`;
    });

    if (isExactMakePage) return;

    this.collectionTitles.forEach((title) => {
      const existingSpan = title.querySelector('.collection__title-brand');
      if (existingSpan) {
        existingSpan.remove();
      }

      const span = document.createElement('span');
      span.classList.add('collection__title-brand');
      span.innerHTML = id;
      title.appendChild(span);
    });
  }

  getCarMakeFilters() {
    const activeFilters = document.querySelectorAll('.active-filters-content span[data-type="Značka"]');
    const selectedLabels = [];
    activeFilters.forEach((checkbox) => {
      const label = checkbox.getAttribute('data-name');
      if (label) {
        selectedLabels.push(label);
      }
    });
    return selectedLabels;
  }

  getCarModelFilters() {
    const activeFilters = document.querySelectorAll('.active-filters-content span[data-type="Model"]');
    const selectedModelLabels = [];
    activeFilters.forEach((checkbox) => {
      const label = checkbox.getAttribute('data-name');
      if (label) {
        selectedModelLabels.push(label);
      }
    });
    return selectedModelLabels;
  }

  countActiveFilters() {
    const activeFilterCount = this.getCarMakeFilters().length;
    return activeFilterCount;
  }

  countActiveModelFilters() {
    const activeModelFilterCount = this.getCarModelFilters().length;
    return activeModelFilterCount;
  }

  updateFilters() {
    const activeMakeFilters = this.getCarMakeFilters();
    const activeModelFilters = this.getCarModelFilters();
    const pathname = window.location.pathname;
    const matchedMake = carMakes.find((make) => pathname === `/collections/${make.handle}`);

    this.setAttribute('data-active-filters', this.countActiveFilters());
    this.setAttribute('data-active-model-filters', this.countActiveModelFilters());

    if (activeMakeFilters.length === 1 && activeModelFilters.length === 0) {
      this.el.classList.remove('hidden');
      this.updateBrandText(activeMakeFilters[0]);
      this.hideModelText();
    } else if (activeModelFilters.length === 1 && activeMakeFilters.length === 0) {
      this.el.classList.remove('hidden');
      this.updateModelText(activeModelFilters[0]);
      this.hideBrandText();
      // this.updateCollectionTitle('');
      if (matchedMake) {
        this.updateBrandText(matchedMake.handle);
      }
    } else if (activeMakeFilters.length === 1 && activeModelFilters.length === 1) {
      this.el.classList.remove('hidden');
      this.updateBrandText(activeMakeFilters[0]);
      this.updateModelText(activeModelFilters[0]);
    } else {
      if (matchedMake) {
        this.el.classList.remove('hidden');
        this.updateBrandText(matchedMake.handle);
        this.hideModelText();
      } else {
        this.el.classList.add('hidden');
        // this.updateCollectionTitle('');
      }
    }
  }

  hideBrandText() {
    const brandTextElement = this.el.querySelector('.seo-brand-text__collection');
    if (brandTextElement) {
      const allMakes = this.el.querySelectorAll('.seo-brand-text__collection-text');
      allMakes.forEach((make) => (make.style.display = 'none'));
    }
  }

  hideModelText() {
    const modelTextElement = this.el.querySelector('.seo-brand-text__collection-model');
    if (modelTextElement) {
      const allModels = this.el.querySelectorAll('.seo-brand-text__collection-model-text');
      allModels.forEach((model) => (model.style.display = 'none'));
    }
  }

  updateBrandText(selectedLabel) {
    const brandTextElement = this.el.querySelector('.seo-brand-text__collection');
    // const title = document.querySelector('.js-brand-title');
    // title.textContent = '';
    if (brandTextElement) {
      const allMakes = document.querySelectorAll('.seo-brand-text__collection-text');
      if (!selectedLabel) {
        allMakes.forEach((make) => (make.style.display = 'none'));
        return;
      }

      let matched = false;
      allMakes.forEach((make) => {
        const makeId = make.getAttribute('data-id').toLowerCase();
        const id = selectedLabel.toLowerCase();
        if (id === makeId) {
          make.style.display = 'block';
          matched = make.getAttribute('data-title');
        } else {
          make.style.display = 'none';
        }
      });

      // if (matched && title && !title.textContent.includes(matched)) {
      //   title.textContent += ` ${matched}`;
      //   this.updateCollectionTitle(matched);
      // }
    }
  }

  updateModelText(selectedLabel) {
    const modelTextElement = this.el.querySelector('.seo-brand-text__collection-model');
    if (modelTextElement) {
      const allModels = document.querySelectorAll('.seo-brand-text__collection-model-text');
      if (!selectedLabel) {
        allModels.forEach((model) => (model.style.display = 'none'));
        return;
      }
      allModels.forEach((model) => {
        const modelId = model.getAttribute('data-id').toLowerCase();
        const id = selectedLabel.toLowerCase();
        if (id === modelId) {
          model.style.display = 'block';
        } else {
          model.style.display = 'none';
        }
      });
    }
  }

  observeFilterChanges() {
    const targetNode = document.querySelector('#FacetFiltersForm');
    if (targetNode) {
      const observer = new MutationObserver(() => {
        this.updateFilters();
      });

      observer.observe(targetNode, { childList: true, subtree: true });
    }

    const targetNode2 = document.querySelector('.active-filters');
    if (targetNode2) {
      const observer = new MutationObserver(() => {
        this.updateFilters();
      });

      observer.observe(targetNode2, { childList: true, subtree: true });
    }

    document.body.addEventListener('input', (event) => {
      if (
        event.target.tagName === 'INPUT' &&
        event.target.type === 'checkbox' &&
        (event.target.name.startsWith('filter.p.m.custom.car_make') ||
          event.target.name.startsWith('filter.p.m.custom.car_model'))
      ) {
        requestAnimationFrame(() => {
          this.updateFilters();
        });
      }
    });
  }
}

customElements.define('seo-brand-text', SeoBrandText);

class BaseFilters extends HTMLElement {
  constructor() {
    super();
    this.el = this;
    this.form = this.el.querySelector('form');
  }

  connectedCallback() {
    setTimeout(() => {
      this.sidebarFilters = [...document.querySelectorAll(this.getSidebarSelector())];
      this.sectionFilters = [...this.el.querySelectorAll(this.getSectionSelector())];

      this.mapIds(this.sidebarFilters, this.sectionFilters);
      this.restoreCheckedInputs();
      this.handleFormSubmit();
    }, 500);
  }

  getSidebarSelector() {
    throw new Error('getSidebarSelector must be implemented by child class');
  }

  getSectionSelector() {
    throw new Error('getSectionSelector must be implemented by child class');
  }

  getValueAttribute() {
    return 'value';
  }

  getFilterIdAttribute() {
    return 'data-id';
  }

  mapIds(sidebarFilters, sectionFilters) {
    const valueAttr = this.getValueAttribute();
    const filterIdAttr = this.getFilterIdAttribute();

    sidebarFilters.forEach((el) => {
      const value = el.getAttribute(valueAttr);
      const id = el.getAttribute('id');

      sectionFilters.forEach((filter) => {
        const filterValue = filter.getAttribute(filterIdAttr);
        if (filterValue === value) {
          filter.setAttribute('id', id);
          const item = filter.closest(this.getItemSelector());
          const label = item.querySelector('label');
          if (label) {
            label.setAttribute('for', id);
          }
        }
      });
    });
  }

  handleFormSubmit() {
    const form = this.querySelector('form');

    form.addEventListener('input', (event) => {
      event.preventDefault();

      const searchParams = new URLSearchParams(window.location.search);
      const paramName = event.target.name;
      const paramValue = event.target.value;

      const currentValues = searchParams.getAll(paramName);

      if (event.target.checked) {
        if (!currentValues.includes(paramValue)) {
          searchParams.append(paramName, paramValue);
        }
      } else {
        const updatedValues = currentValues.filter((value) => value !== paramValue);
        searchParams.delete(paramName);
        updatedValues.forEach((value) => searchParams.append(paramName, value));
      }

      this.toggleSelectedClass(event.target);
      this.syncWithFacets(event.target.name, event.target.value, event.target.checked);

      FacetFiltersForm.renderPage(searchParams.toString(), event, true);
    });
  }

  syncWithFacets(name, value, checked) {
    setTimeout(() => {
      const facetInputs = document.querySelectorAll(
        `#FacetFiltersForm input[name="${name}"][value="${value}"], #FacetFiltersFormMobile input[name="${name}"][value="${value}"]`,
      );

      facetInputs.forEach((input) => {
        input.checked = checked;
        const parentLabel = input.closest('label');
        if (parentLabel) {
          if (checked) {
            parentLabel.classList.add('selected');
          } else {
            parentLabel.classList.remove('selected');
          }
        }
      });
    }, 50);
  }

  restoreCheckedInputs() {
    const searchParams = new URLSearchParams(window.location.search);

    this.sectionFilters.forEach((input) => {
      const paramName = input.name;
      const paramValue = input.value;

      if (searchParams.getAll(paramName).includes(paramValue)) {
        input.checked = true;
      } else {
        input.checked = false;
      }

      this.toggleSelectedClass(input);
    });
  }

  updateActiveFilters() {
    const sectionFilters = document.querySelectorAll(this.getSectionSelector());
    const searchParams = new URLSearchParams(window.location.search);
    const params = {};

    searchParams.forEach((value, key) => {
      if (!params[key]) {
        params[key] = [];
      }
      params[key].push(value);
    });

    sectionFilters.forEach((input) => {
      const inputName = input.name;
      const inputValue = input.value;

      if (params[inputName] && params[inputName].includes(inputValue)) {
        input.checked = true;
      } else {
        input.checked = false;
      }
    });
  }

  toggleSelectedClass(input) {
    const item = input.closest(this.getItemSelector());
    if (item) {
      if (input.checked) {
        item.classList.add('selected');
      } else {
        item.classList.remove('selected');
      }
    }
  }
}

class BrandFilters extends BaseFilters {
  connectedCallback() {
    super.connectedCallback();
    this.updateProductCount();
  }

  updateProductCount() {
    const originalCounts = document.querySelectorAll('#FacetsWrapperDesktop .js-original-count');
    const brandsItems = Array.from(document.querySelectorAll('.brands-item'));

    brandsItems.forEach((item) => {
      const itemId = item.getAttribute('data-name').toLowerCase();
      const countElement = item.querySelector('.brands-list__list-item-badge span');

      let count = 0;

      originalCounts.forEach((filter) => {
        if (filter.getAttribute('data-name').toLowerCase() === itemId) {
          count = parseInt(filter.textContent.trim(), 10) || 0;
        }
      });

      if (countElement) {
        countElement.textContent = count;
      }

      if (count === 0) {
        item.classList.add('no-items');
      } else {
        item.classList.remove('no-items');
      }
    });
  }

  getSidebarSelector() {
    return "#FacetsWrapperDesktop input[name='filter.p.m.custom.car_make']";
  }

  getSectionSelector() {
    return '.brand-filters__input';
  }

  getValueAttribute() {
    return 'data-id';
  }

  getFilterIdAttribute() {
    return 'data-id';
  }

  getItemSelector() {
    return '.brands-list__list-item';
  }
}

class CategoryFilters extends BaseFilters {
  getSidebarSelector() {
    return "#FacetsWrapperDesktop input[name='filter.p.m.custom.car_type']";
  }

  getSectionSelector() {
    return '.category-filters__input';
  }

  getValueAttribute() {
    return 'value';
  }

  getFilterIdAttribute() {
    return 'value';
  }

  getItemSelector() {
    return '.category-filters__item';
  }
}

customElements.define('brand-filters', BrandFilters);
customElements.define('category-filters', CategoryFilters);

class StickyBuyBar extends HTMLElement {
  constructor() {
    super();

    this.el = this;
    this.target = document.querySelector('.product .product-form');
    this.productBuyButtons = document.querySelector('.product .product__buy-buttons');
    this.form = this.querySelector('form');
    this.variantIdInput = this.form.querySelector('input[name="id"]');
    this.compareBar = document.querySelector('.product-comparison-bar');

    this.scrollEventHandler();
    this.updateVariantId();
    this.initCompareBarObserver();

    if (this.el.classList.contains('hidden')) {
      this.removeClassFromBody();
    } else {
      this.addClassToBody();
    }
  }

  compareBarObserver() {
    if (this.compareBar.classList.contains('is-visible')) {
      this.el.style.top = '0';
      this.el.style.bottom = 'auto';
    } else {
      this.el.style.top = 'auto';
      this.el.style.bottom = '0';
    }
  }

  initCompareBarObserver() {
    const target = this.compareBar;

    const observer = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          this.compareBarObserver();
        }
      }
    });

    observer.observe(target, {
      attributes: true,
      attributeFilter: ['class'],
    });

    this.compareBarObserver();
  }

  scrollEventHandler() {
    const productInfo = document.querySelector('product-info');

    if (!productInfo) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio === 0) {
          this.el.classList.remove('sticky-buy-bar-hidden');
        } else {
          this.el.classList.add('sticky-buy-bar-hidden');
        }
      },
      {
        root: null,
        threshold: 0,
      },
    );

    observer.observe(productInfo);
  }

  updateVariantId() {
    document.addEventListener('variant:updated', (event) => {
      const variant = event.detail.variant;
      this.variantIdInput.value = variant.id;

      this.isAvailable = variant.available;

      if (!this.isAvailable) {
        this.el.classList.add('hidden');
        this.removeClassFromBody();
      } else {
        this.el.classList.remove('hidden');
        this.addClassToBody();
      }
    });
  }

  addClassToBody() {
    document.body.classList.add('sticky-buy-bar-active');
  }

  removeClassFromBody() {
    document.body.classList.remove('sticky-buy-bar-active');
  }
}

customElements.define('sticky-buy-bar', StickyBuyBar);

class CustomDropdown extends HTMLElement {
  constructor() {
    super();
    this.trigger = this.querySelector('.dropdown-trigger');
    this.content = this.querySelector('.dropdown-content');
    this.inputs = this.querySelectorAll('input[type="radio"]');

    this.trigger.addEventListener('click', this.toggleDropdown.bind(this));
    document.addEventListener('click', this.closeIfClickedOutside.bind(this));

    if (this.classList.contains('sorting-dropdown')) {
      this.inputs.forEach((input) => {
        input.addEventListener('change', this.handleSort.bind(this));
      });
    }
  }

  handleSort(event) {
    const form = document.createElement('form');
    form.method = 'get';

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'sort_by';
    input.value = event.target.value;

    form.appendChild(input);
    document.body.appendChild(form);
    form.submit();
  }

  toggleDropdown(event) {
    event.preventDefault();
    const isExpanded = this.trigger.getAttribute('aria-expanded') === 'true';
    this.trigger.setAttribute('aria-expanded', !isExpanded);
  }

  closeIfClickedOutside(event) {
    if (!this.contains(event.target)) {
      this.trigger.setAttribute('aria-expanded', 'false');
    }
  }
}

customElements.define('custom-dropdown', CustomDropdown);

class CustomDropdownSorting extends HTMLElement {
  constructor() {
    super();
    this.bindEvents();
  }

  bindEvents() {
    const trigger = this.querySelector('.dropdown-trigger');
    const dropdownContent = this.querySelector('.dropdown-content');
    const inputs = this.querySelectorAll('input[type="radio"], input[type="checkbox"]');

    if (trigger) {
      trigger.addEventListener('click', this.toggleDropdown.bind(this));
    }

    document.addEventListener('click', this.closeIfClickedOutside.bind(this));

    inputs.forEach((input) => {
      if (this.classList.contains('sorting-dropdown')) {
        input.addEventListener('change', this.handleSort.bind(this));
      } else {
        input.addEventListener('change', () => {
          // Update dropdown trigger text for non-sorting dropdowns
          const selectedText = input.closest('label').querySelector('.dropdown-text').textContent;
          if (trigger) {
            trigger.childNodes[0].textContent = selectedText.trim();
          }
          // Close dropdown
          this.querySelector('.dropdown-content').classList.remove('active');
          this.querySelector('.dropdown-trigger').setAttribute('aria-expanded', 'false');
        });
      }
    });
  }

  handleSort(event) {
    event.preventDefault();
    const selectedValue = event.target.value;
    const currentUrl = new URL(window.location.href);
    const params = new URLSearchParams(currentUrl.search);

    params.set('sort_by', selectedValue);

    const newUrl = `${currentUrl.pathname}?${params.toString()}`;
    window.history.pushState({ path: newUrl }, '', newUrl);

    const selectedText = event.target.closest('label').querySelector('.dropdown-text').textContent;
    const trigger = this.querySelector('.dropdown-trigger');
    if (trigger) {
      trigger.childNodes[0].textContent = selectedText.trim();
    }

    this.querySelector('.dropdown-content').classList.remove('active');
    this.querySelector('.dropdown-trigger').setAttribute('aria-expanded', 'false');

    const sortEvent = new CustomEvent('sort-update', {
      detail: { searchParams: params.toString() },
    });
    document.dispatchEvent(sortEvent);
  }

  toggleDropdown(event) {
    event.preventDefault();
    const trigger = event.currentTarget;
    const content = this.querySelector('.dropdown-content');
    const isExpanded = trigger.getAttribute('aria-expanded') === 'true';

    trigger.setAttribute('aria-expanded', !isExpanded);
  }

  closeIfClickedOutside(event) {
    if (!this.contains(event.target)) {
      const content = this.querySelector('.dropdown-content');
      const trigger = this.querySelector('.dropdown-trigger');
      if (content && content.classList.contains('active')) {
        content.classList.remove('active');
        trigger.setAttribute('aria-expanded', 'false');
      }
    }
  }
}

customElements.define('custom-dropdown-sorting', CustomDropdownSorting);

// Header compare count
function updateCompareCounts() {
  const countElements = document.querySelectorAll('.js-compare-count');
  const bubbleElements = document.querySelectorAll('.js-compare-bubble');

  if (countElements.length === 0) {
    return;
  }

  try {
    const compareList = JSON.parse(localStorage.getItem('productComparison') || '[]');
    const count = compareList.length;
    const showBubble = count > 0;

    countElements.forEach((element) => {
      element.textContent = count;
    });

    bubbleElements.forEach((bubble) => {
      if (showBubble) {
        bubble.classList.remove('visually-hidden');
      } else {
        bubble.classList.add('visually-hidden');
      }
    });
  } catch (error) {
    console.error('Error updating compare counts:', error);
    countElements.forEach((element) => {
      element.textContent = '0';
    });
    bubbleElements.forEach((bubble) => {
      bubble.classList.add('visually-hidden');
    });
  }
}

document.addEventListener('DOMContentLoaded', updateCompareCounts);
window.addEventListener('compareListUpdated', updateCompareCounts);
window.addEventListener('compareListCleared', updateCompareCounts);
window.addEventListener('storage', (e) => {
  if (e.key === 'productComparison') {
    updateCompareCounts();
  }
});

function checkWishlistEmptyState() {
  const wishlistCounter = document.getElementById('wishlist-products-count');
  const emptyWishlistElement = document.querySelector('.empty-wishlist');

  if (!wishlistCounter || !emptyWishlistElement) return;

  const count = parseInt(wishlistCounter.textContent) || 0;

  if (count === 0) {
    emptyWishlistElement.classList.remove('hidden');
  } else {
    emptyWishlistElement.classList.add('hidden');
  }
}

document.addEventListener('DOMContentLoaded', checkWishlistEmptyState);

document.addEventListener('DOMContentLoaded', () => {
  const wishlistCounter = document.getElementById('wishlist-products-count');
  if (!wishlistCounter) return;

  const observer = new MutationObserver(checkWishlistEmptyState);
  observer.observe(wishlistCounter, { childList: true, characterData: true, subtree: true });
});

document.addEventListener('DOMContentLoaded', () => {
  document.addEventListener('click', (event) => {
    const facetRemove = event.target.closest('.active-filters-content facet-remove');
    if (facetRemove) {
      setTimeout(() => {
        const brandFilters = document.querySelector('brand-filters');
        const categoryFilters = document.querySelector('category-filters');

        if (brandFilters) {
          const brandInputs = brandFilters.querySelectorAll('.brand-filters__input');
          updateCustomFilters(brandInputs, brandFilters);
        }

        if (categoryFilters) {
          const categoryInputs = categoryFilters.querySelectorAll('.category-filters__input');
          updateCustomFilters(categoryInputs, categoryFilters);
        }
      }, 200);
    }

    const clearAll = event.target.closest('.active-filters-clear');
    if (clearAll) {
      setTimeout(() => {
        const brandFilters = document.querySelector('brand-filters');
        const categoryFilters = document.querySelector('category-filters');

        if (brandFilters) {
          const brandInputs = brandFilters.querySelectorAll('.brand-filters__input');
          brandInputs.forEach((input) => {
            input.checked = false;
            if (brandFilters.toggleSelectedClass) {
              brandFilters.toggleSelectedClass(input);
            }
          });
        }

        if (categoryFilters) {
          const categoryInputs = categoryFilters.querySelectorAll('.category-filters__input');
          categoryInputs.forEach((input) => {
            input.checked = false;
            if (categoryFilters.toggleSelectedClass) {
              categoryFilters.toggleSelectedClass(input);
            }
          });
        }
      }, 200);
    }
  });
});

function updateCustomFilters(inputs, filterComponent) {
  if (!inputs || !filterComponent) return;

  const searchParams = new URLSearchParams(window.location.search);
  const activeParams = new Map();

  searchParams.forEach((value, key) => {
    if (!activeParams.has(key)) {
      activeParams.set(key, []);
    }
    activeParams.get(key).push(value);
  });

  inputs.forEach((input) => {
    const paramName = input.name;
    const paramValue = input.getAttribute(filterComponent.getFilterIdAttribute()) || input.value;
    const paramValues = activeParams.get(paramName) || [];

    const isActive = paramValues.some((value) => value.toLowerCase() === paramValue.toLowerCase());

    input.checked = isActive;
    if (filterComponent.toggleSelectedClass) {
      filterComponent.toggleSelectedClass(input);
    }
  });
}

// Sync desktop wishlist counter to mobile counter
document.addEventListener('DOMContentLoaded', () => {
  document.querySelector('.header__icon--menu')?.addEventListener('click', () => {
    setTimeout(() => {
      const count = document.getElementById('wishlist-products-count')?.textContent || '0';
      const mobile = document.getElementById('wishlist-products-count-mobile');
      if (mobile) mobile.textContent = count;
    }, 100);
  });
});
